import { Star } from "lucide-react";

/**
 * ReviewsMiniBar — compact star row.
 * Replaces the giant BC Score card. When there are no reviews, shows an
 * unobtrusive `☆☆☆☆☆ —/5 (0)` row with a "Write a review" link (hidden
 * for owners). When there are reviews, shows `★ 4.8 (27 reviews)` with a
 * "See Reviews" link.
 *
 * Height stays between 44–60px so it never dominates the storefront.
 */
export function ReviewsMiniBar({
  score = 0,
  count = 0,
  canReview = true,
  onWriteReview,
  onSeeReviews,
  testId = "reviews-mini-bar",
}) {
  const hasReviews = count > 0;
  const fullStars = Math.floor(score);
  const hasHalf = !Number.isInteger(score) && score % 1 >= 0.4;

  return (
    <div
      className="flex items-center justify-between gap-3 rounded-full border border-tbc-gold/25 bg-white px-4 py-2 min-h-[48px]"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center" aria-hidden="true">
          {Array.from({ length: 5 }, (_, i) => (
            <Star
              key={`star-${i}`}
              className={`w-4 h-4 ${
                i < fullStars
                  ? "fill-tbc-gold text-tbc-gold"
                  : i === fullStars && hasHalf
                    ? "fill-tbc-gold/50 text-tbc-gold"
                    : "text-gray-300"
              }`}
            />
          ))}
        </div>
        <span className="font-heading text-sm text-tbc-navy whitespace-nowrap">
          {hasReviews ? score.toFixed(1) : "—"}/5
        </span>
        <span className="text-xs text-gray-500 font-body whitespace-nowrap">
          ({count})
        </span>
      </div>

      {hasReviews ? (
        <button
          type="button"
          onClick={onSeeReviews}
          className="text-xs sm:text-sm font-medium text-tbc-navy hover:text-tbc-gold whitespace-nowrap"
          data-testid="see-reviews-link"
        >
          See Reviews →
        </button>
      ) : canReview && onWriteReview ? (
        <button
          type="button"
          onClick={onWriteReview}
          className="text-xs sm:text-sm font-medium text-tbc-gold hover:text-tbc-navy whitespace-nowrap"
          data-testid="write-review-link"
        >
          Write a review →
        </button>
      ) : null}
    </div>
  );
}

export default ReviewsMiniBar;
